import type { MrpInventoryRow } from "@/lib/mrp";

import type { TransferBoardLocation } from "./transfersWorkbenchTypes";

export const TRANSFER_ORIGIN_ON_HAND_HOVER_HINT =
  "Physical count at the transfer origin site for this stock keeping unit on the latest hub snapshot (Megaventory StockPhysical on mrp.inventory_snapshot), summed across SubLocations. This is what is in the building, not a reservation or planning on-hand.";

/** Sum Megaventory StockPhysical per SKU at one site (all SubLocation rows). */
export function aggregatePhysicalOnHandBySku(rows: MrpInventoryRow[]): Map<string, number> {
  const totals = new Map<string, number>();
  for (const row of rows) {
    const skuKey = String(row.cpn).trim().toLowerCase();
    if (skuKey === "") continue;
    const physical = Number(row.stock_physical);
    if (!Number.isFinite(physical)) continue;
    totals.set(skuKey, (totals.get(skuKey) ?? 0) + physical);
  }
  return totals;
}

export function normalizeMrpSiteCode(raw: string | null | undefined): string | null {
  const trimmed = raw?.trim().toLowerCase() ?? "";
  return trimmed === "" ? null : trimmed;
}

export function mrpSiteCodeForLocationId(
  locationsById: Map<number, TransferBoardLocation>,
  locationId: number,
): string | null {
  return normalizeMrpSiteCode(locationsById.get(locationId)?.mrp_site_code);
}

export function lookupOriginPhysicalOnHand(
  inventoryBySite: ReadonlyMap<string, ReadonlyMap<string, number>>,
  siteCode: string | null,
  sku: string | null | undefined,
): number | null {
  if (siteCode == null) return null;
  const skuKey = sku?.trim().toLowerCase() ?? "";
  if (skuKey === "") return null;
  const bucket = inventoryBySite.get(siteCode);
  if (bucket == null) return null;
  return bucket.get(skuKey) ?? 0;
}

export function formatTransferOriginOnHand(
  value: number | null,
  options: { loading?: boolean; siteMissing?: boolean } = {},
): string {
  if (options.loading) return "…";
  if (options.siteMissing) return "—";
  if (value == null) return "—";
  return String(Math.round(value));
}

export function originOnHandExceedsQuantity(onHand: number | null, quantity: number): boolean {
  if (onHand == null || !Number.isFinite(quantity)) return false;
  return quantity > onHand;
}

/**
 * True when any SKU on a still-shipping transfer needs more origin on-hand
 * than the latest physical snapshot. Same-SKU lines are summed.
 */
export function transferOrderHasOriginInventoryShortfall(
  order: {
    status: string;
    origin_location_id: number;
    lines: readonly { sku_id: string | null; quantity: number; approved_pack_quantity: number | null }[];
  },
  lookup: TransferOriginOnHandLookup,
  options: { loading?: boolean } = {},
): boolean {
  if (options.loading) return false;
  if (
    order.status === "shipped"
    || order.status === "delivered"
    || order.status === "partially_received"
    || order.status === "received"
    || order.status === "cancelled"
  ) {
    return false;
  }
  const neededBySku = new Map<string, number>();
  for (const line of order.lines) {
    const skuKey = line.sku_id?.trim().toLowerCase() ?? "";
    if (skuKey === "") continue;
    const packTarget =
      line.approved_pack_quantity != null
      && Number.isInteger(line.approved_pack_quantity)
      && line.approved_pack_quantity > 0
        ? line.approved_pack_quantity
        : line.quantity;
    if (!Number.isFinite(packTarget)) continue;
    neededBySku.set(skuKey, (neededBySku.get(skuKey) ?? 0) + packTarget);
  }
  for (const [sku, needed] of neededBySku) {
    const originOnHand = lookup(order.origin_location_id, sku);
    if (originOnHand.siteMissing) continue;
    if (originOnHandExceedsQuantity(originOnHand.value, needed)) return true;
  }
  return false;
}

export type TransferOriginOnHandLookup = (
  locationId: number,
  sku: string | null | undefined,
) => { value: number | null; siteMissing: boolean };

export function buildTransferOriginOnHandLookup(
  locationsById: Map<number, TransferBoardLocation>,
  inventoryBySite: ReadonlyMap<string, ReadonlyMap<string, number>>,
): TransferOriginOnHandLookup {
  return (locationId, sku) => {
    const siteCode = mrpSiteCodeForLocationId(locationsById, locationId);
    if (siteCode == null) {
      return { value: null, siteMissing: true };
    }
    return {
      value: lookupOriginPhysicalOnHand(inventoryBySite, siteCode, sku),
      siteMissing: false,
    };
  };
}
